import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { updateSpreadsheetId } from "../../../services/product";
import { FaGoogle } from "react-icons/fa";

const LOCAL_STORAGE_FIELD_KEYS = "sheetFields";
const TOKEN_STORAGE_KEY = "google_token";
const SHEET_ID_STORAGE_KEY = "spreadsheetId";

const GoogleSheetsAPI = ({
  fieldData,
  setFieldData,
  spreadsheetId,
  setSpreadsheetId,
  defaultFieldsData,
}) => {
  const [token, setToken] = useState(() => {
    return (
      localStorage.getItem(TOKEN_STORAGE_KEY) ||
      localStorage.getItem("token") ||
      ""
    );
  });
  const [sheetId, setSheetId] = useState(spreadsheetId || null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [tokenClient, setTokenClient] = useState(null);
  const sheetTitle = "Sheet1";

  useEffect(() => {
    setSheetId(spreadsheetId);
  }, [spreadsheetId]);

  useEffect(() => {
    const existingScript = document.getElementById("google-identity-script");
    if (existingScript) {
      if (window.google?.accounts?.oauth2) {
        setScriptLoaded(true);
      } else {
        existingScript.addEventListener("load", () => setScriptLoaded(true));
      }
      return;
    }

    const script = document.createElement("script");
    script.id = "google-identity-script";
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => setScriptLoaded(true);
    script.onerror = () => {
      toast.error("Failed to load Google authentication script.");
    };
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    if (!scriptLoaded) return;
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      toast.error("Google Client ID missing. Please set REACT_APP_GOOGLE_CLIENT_ID.");
      return;
    }

    try {
      const client = window.google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: "https://www.googleapis.com/auth/spreadsheets",
        callback: () => {},
      });
      setTokenClient(client);
    } catch (error) {
      console.error("Failed to initialise Google OAuth client", error);
      toast.error("Google authentication initialisation failed.");
    }
  }, [scriptLoaded]);

  const acquireToken = async (prompt = "consent") => {
    if (!tokenClient) {
      throw new Error("Google authentication not ready. Try again shortly.");
    }

    setIsAuthorizing(prompt === "consent");

    return new Promise((resolve, reject) => {
      tokenClient.callback = (tokenResponse) => {
        setIsAuthorizing(false);
        if (tokenResponse?.access_token) {
          setToken(tokenResponse.access_token);
          localStorage.setItem(TOKEN_STORAGE_KEY, tokenResponse.access_token);
          resolve(tokenResponse.access_token);
        } else {
          reject(new Error("Failed to acquire Google token."));
        }
      };

      try {
        tokenClient.requestAccessToken({ prompt });
      } catch (error) {
        setIsAuthorizing(false);
        reject(error);
      }
    });
  };

  const ensureToken = async () => {
    if (token) return token;
    return acquireToken("");
  };

  const handleAuthClick = async () => {
    try {
      await acquireToken("consent");
      toast.success("Google Sheets authorized");
    } catch (error) {
      console.error("Authentication failed:", error);
      toast.error(error.message || "Google authentication failed. Please try again.");
    }
  };

  const handleSignoutClick = () => {
    if (!token) return;
    if (!window.google?.accounts?.oauth2) {
      toast.error("Google services not ready.");
      return;
    }

    setLoading(true);
    window.google.accounts.oauth2.revoke(token, () => {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem("token");
      localStorage.removeItem(SHEET_ID_STORAGE_KEY);
      setToken(null);
      setSheetId(null);
      toast.success("Signed out successfully. You can now choose a different account.");
      setLoading(false);
    });
  };

  const makeSheetsApiRequest = async (url, method = "GET", body = null) => {
    const accessToken = await ensureToken();

    const headers = {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    };

    const options = {
      method,
      headers,
    };

    if (body) {
      options.body = JSON.stringify(body);
    }

    const response = await fetch(url, options);

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throw new Error(errorBody?.error?.message || "Google Sheets request failed.");
    }

    return response.json();
  };

  const sheetExists = async (id) => {
    if (!id) return false;

    try {
      await makeSheetsApiRequest(
        `https://sheets.googleapis.com/v4/spreadsheets/${id}`
      );
      return true;
    } catch {
      return false;
    }
  };

  const createHeader = async (id, headers) => {
    if (!id) return;

    const range = `${sheetTitle}!A1:${String.fromCharCode(
      65 + headers.length - 1
    )}1`;

    await makeSheetsApiRequest(
      `https://sheets.googleapis.com/v4/spreadsheets/${id}/values/${range}?valueInputOption=RAW`,
      "PUT",
      {
        values: [headers],
      }
    );
  };

  const initializeSpreadsheet = async () => {
    const isExists = await sheetExists(spreadsheetId);
    if (isExists) {
      localStorage.setItem(SHEET_ID_STORAGE_KEY, spreadsheetId);
      setSheetId(spreadsheetId);
      return spreadsheetId;
    }

    const creationToast = toast.loading("Creating new spreadsheet");
    try {
      const response = await makeSheetsApiRequest(
        "https://sheets.googleapis.com/v4/spreadsheets",
        "POST",
        {
          properties: {
            title: "Scan Profit",
          },
        }
      );

      setSpreadsheetId(response.spreadsheetId);
      await updateSpreadsheetId(response.spreadsheetId);

      const headersArray = Object.keys(defaultFieldsData);
      await createHeader(response.spreadsheetId, headersArray);

      setSheetId(response.spreadsheetId);
      localStorage.setItem(SHEET_ID_STORAGE_KEY, response.spreadsheetId);

      toast.success("Spreadsheet created successfully");
      return response.spreadsheetId;
    } catch (error) {
      toast.error(error.message || "Failed to create spreadsheet.");
      throw error;
    } finally {
      toast.dismiss(creationToast);
    }
  };

  const writeData = async (values) => {
    const exportToast = toast.loading("Exporting fieldData...");
    let ssId = null;

    try {
      ssId = await initializeSpreadsheet();
      const range = `${sheetTitle}!A:A`;
      let lastRow = 0;

      const response = await makeSheetsApiRequest(
        `https://sheets.googleapis.com/v4/spreadsheets/${ssId}/values/${range}`
      );

      if (response.values) {
        lastRow = response.values.length;
      }

      const appendRange = `${sheetTitle}!A${lastRow + 1}:B${lastRow + 1}`;
      await makeSheetsApiRequest(
        `https://sheets.googleapis.com/v4/spreadsheets/${ssId}/values/${appendRange}:append?valueInputOption=RAW&insertDataOption=INSERT_ROWS`,
        "POST",
        {
          values,
        }
      );

      toast.success("Data exported successfully");
    } catch (err) {
      console.error("Error exporting data:", err);
      toast.error(err.message || "Something went wrong!");
    } finally {
      toast.dismiss(exportToast);
    }
  };

  const writeDataHandler = async () => {
    try {
      await ensureToken();
    } catch (err) {
      toast.error(err.message);
      return;
    }

    const storedFieldKeys =
      JSON.parse(localStorage.getItem(LOCAL_STORAGE_FIELD_KEYS)) ||
      Object.keys(defaultFieldsData);

    const storedKeysSet = new Set(storedFieldKeys);
    const valuesArray = Object.keys(defaultFieldsData).map((key) => {
      return storedKeysSet.has(key) ? defaultFieldsData[key] || "-" : "-";
    });

    await writeData([valuesArray]);
  };

  const handleConfigure = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const removeField = (key) => {
    const updated = { ...fieldData };
    delete updated[key];
    setFieldData(updated);
    const storedKeys = Object.keys(updated);
    localStorage.setItem(LOCAL_STORAGE_FIELD_KEYS, JSON.stringify(storedKeys));
  };

  const handleFieldChange = (oldKey, newKey) => {
    if (newKey === oldKey) return;

    const updated = { ...fieldData };
    const value = updated[oldKey];
    delete updated[oldKey];
    updated[newKey] = value;
    setFieldData(updated);
    const storedKeys = Object.keys(updated);
    localStorage.setItem(LOCAL_STORAGE_FIELD_KEYS, JSON.stringify(storedKeys));
  };

  const addNewField = () => {
    const newField = defaultFieldOptions.find(
      (option) => !fieldData.hasOwnProperty(option)
    );
    if (newField) {
      setFieldData((prev) => {
        const updated = {
          ...prev,
          [newField]: defaultFieldsData[newField] || "",
        };
        const storedKeys = Object.keys(updated);
        localStorage.setItem(
          LOCAL_STORAGE_FIELD_KEYS,
          JSON.stringify(storedKeys)
        );
        return updated;
      });
    }
  };

  const defaultFieldOptions = useMemo(
    () => Object.keys(defaultFieldsData || {}),
    [defaultFieldsData]
  );

  return (
    <>
      <div className="flex flex-col w-full">
        <button
          className={`group bg-primary text-primary-bg py-3 px-4 rounded-lg mt-2 ${
            isAuthorizing || !scriptLoaded ? "opacity-75 cursor-not-allowed" : ""
          }`}
          onClick={handleAuthClick}
          style={{ display: token ? "none" : "block" }}
          disabled={isAuthorizing || !scriptLoaded}
        >
          {isAuthorizing ? (
            <div className="flex items-center justify-center space-x-2">
              <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin group-hover:border-secondary"></div>
              <span className="group-hover:text-secondary">Authorizing...</span>
            </div>
          ) : (
            <div className="flex items-center font-semibold text-sm justify-center space-x-2">
              <FaGoogle className="w-4 h-4" />
              <span>Authorize Google Sheets</span>
            </div>
          )}
        </button>
        {token && (
          <button
            className="bg-primary text-sm font-semibold text-primary-bg py-2 px-4 mb-1 rounded-lg mt-2"
            onClick={handleConfigure}
          >
            Configure
          </button>
        )}
        {token && (
          <button
            className="bg-primary text-sm font-semibold text-primary-bg py-2 px-4 mb-1 rounded-lg mt-2"
            onClick={writeDataHandler}
          >
            Export Data
          </button>
        )}
        {sheetId && token && (
          <a
            className="bg-primary text-center text-sm font-semibold text-primary-bg py-2 px-4 mb-1 rounded-lg mt-2"
            href={`https://docs.google.com/spreadsheets/d/${sheetId}/edit`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open Sheet
          </a>
        )}
        <button
          className={`bg-primary text-sm font-semibold text-primary-bg py-2 px-4 rounded-lg mt-2 ${
            loading ? "opacity-75 cursor-not-allowed" : ""
          }`}
          onClick={handleSignoutClick}
          style={{ display: token ? "block" : "none" }}
          disabled={loading}
        >
          {loading ? (
            <div className="flex items-center justify-center space-x-2">
              <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
              <span>Signing out...</span>
            </div>
          ) : (
            "Turn Off Google Sheets"
          )}
        </button>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
            onClick={closeModal}
          />
          <div className="fixed inset-0 flex justify-center items-center z-50">
            <div
              className="bg-primary-bg border border-primary rounded-lg p-6 w-11/12 max-w-md relative"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-lg text-primary font-semibold mb-4">
                Manage Fields
              </h2>

              {Object.entries(fieldData).length === 0 && (
                <p className="text-gray-500">No fields available.</p>
              )}

              {Object.entries(fieldData).map(([key]) => (
                <div key={key} className="flex items-center mb-3 space-x-2">
                  <select
                    className="border text-primary bg-primary-bg border-primary rounded-md px-2 py-1 flex-grow"
                    value={key}
                    onChange={(e) => handleFieldChange(key, e.target.value)}
                  >
                    {defaultFieldOptions.map((optionKey) => (
                      <option key={optionKey} value={optionKey}>
                        {optionKey}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => removeField(key)}
                    className="text-red-600 hover:text-red-800 font-bold text-lg"
                    aria-label={`Remove field ${key}`}
                  >
                    &times;
                  </button>
                </div>
              ))}

              <div>
                <button
                  onClick={addNewField}
                  className="mt-4 bg-secondary text-primary py-2 px-4 rounded-md hover:bg-primary hover:text-secondary transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-secondary disabled:hover:text-primary"
                  disabled={
                    Object.keys(fieldData).length >= defaultFieldOptions.length
                  }
                >
                  Add Field
                </button>
                <button
                  onClick={closeModal}
                  className="mt-4 mx-2 bg-secondary text-primary py-2 px-4 rounded-md hover:bg-primary hover:text-secondary transition duration-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default GoogleSheetsAPI;
